import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  Select,
  DatePicker,
  Tag,
  message,
  Popconfirm,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  DeleteOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import * as logService from '../services/logService';

const { RangePicker } = DatePicker;

const LoginLog: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<logService.LoginLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  // 搜索条件
  const [searchUsername, setSearchUsername] = useState('');
  const [searchIP, setSearchIP] = useState('');
  const [searchStatus, setSearchStatus] = useState<string>('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await logService.listLoginLogs({
        page,
        pageSize,
        username: searchUsername || undefined,
        ip: searchIP || undefined,
        status: searchStatus || undefined,
        startTime: dateRange?.[0]?.format('YYYY-MM-DD 00:00:00'),
        endTime: dateRange?.[1]?.format('YYYY-MM-DD 23:59:59'),
      });
      setLogs(res.data.list || []);
      setTotal(res.data.total || 0);
    } catch {
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [page, pageSize]);

  const handleSearch = () => {
    setPage(1);
    loadData();
  };

  const handleReset = () => {
    setSearchUsername('');
    setSearchIP('');
    setSearchStatus('');
    setDateRange(null);
    setPage(1);
    loadData();
  };

  const handleDelete = async () => {
    if (selectedKeys.length === 0) {
      message.warning('请选择要删除的记录');
      return;
    }
    try {
      await logService.deleteLoginLogs(selectedKeys);
      message.success('删除成功');
      setSelectedKeys([]);
      loadData();
    } catch {
      message.error('删除失败');
    }
  };

  const handleClear = async () => {
    try {
      await logService.clearLoginLogs();
      message.success('清空成功');
      loadData();
    } catch {
      message.error('清空失败');
    }
  };

  const columns: ColumnsType<logService.LoginLog> = [
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 120,
    },
    {
      title: '登录IP',
      dataIndex: 'ip',
      key: 'ip',
      width: 130,
    },
    {
      title: '登录地点',
      dataIndex: 'location',
      key: 'location',
      width: 150,
      render: (location) => location || '-',
    },
    {
      title: '浏览器',
      dataIndex: 'browser',
      key: 'browser',
      width: 120,
      ellipsis: true,
      render: (browser) => (
        <Tooltip title={browser}>{browser || '-'}</Tooltip>
      ),
    },
    {
      title: '操作系统',
      dataIndex: 'os',
      key: 'os',
      width: 120,
      ellipsis: true,
      render: (os) => (
        <Tooltip title={os}>{os || '-'}</Tooltip>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status) => (
        <Tag color={status === 1 ? 'green' : 'red'}>
          {status === 1 ? '成功' : '失败'}
        </Tag>
      ),
    },
    {
      title: '消息',
      dataIndex: 'message',
      key: 'message',
      width: 150,
      ellipsis: true,
      render: (msg) => (
        <Tooltip title={msg}>{msg || '-'}</Tooltip>
      ),
    },
    {
      title: '登录时间',
      dataIndex: 'loginTime',
      key: 'loginTime',
      width: 170,
      render: (time) => dayjs(time).format('YYYY-MM-DD HH:mm:ss'),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card title="登录日志">
        <Space style={{ marginBottom: 16 }} wrap>
          <Input
            placeholder="用户名"
            value={searchUsername}
            onChange={(e) => setSearchUsername(e.target.value)}
            style={{ width: 120 }}
            allowClear
          />
          <Input
            placeholder="登录IP"
            value={searchIP}
            onChange={(e) => setSearchIP(e.target.value)}
            style={{ width: 130 }}
            allowClear
          />
          <Select
            placeholder="状态"
            value={searchStatus || undefined}
            onChange={setSearchStatus}
            style={{ width: 100 }}
            allowClear
          >
            <Select.Option value="1">成功</Select.Option>
            <Select.Option value="0">失败</Select.Option>
          </Select>
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null])}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            搜索
          </Button>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>
            重置
          </Button>
        </Space>

        <Space style={{ marginBottom: 16 }}>
          <Popconfirm
            title={`确定删除选中的 ${selectedKeys.length} 条记录？`}
            onConfirm={handleDelete}
            disabled={selectedKeys.length === 0}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              disabled={selectedKeys.length === 0}
            >
              批量删除
            </Button>
          </Popconfirm>
          <Popconfirm title="确定清空所有登录日志？此操作不可恢复！" onConfirm={handleClear}>
            <Button danger icon={<ClearOutlined />}>
              清空日志
            </Button>
          </Popconfirm>
          <Button icon={<ReloadOutlined />} onClick={loadData}>
            刷新
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          loading={loading}
          rowSelection={{
            selectedRowKeys: selectedKeys,
            onChange: (keys) => setSelectedKeys(keys as string[]),
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
          scroll={{ x: 1100 }}
          size="small"
        />
      </Card>
    </div>
  );
};

export default LoginLog;
